/**
 * CheckoutConfirm - Modal de confirmación de cierre por el rival
 * 501 Dart League Online
 * 
 * Cuando un jugador marca 0 (cierre), el rival debe confirmar que vio
 * el doble de cierre en el video. Sin auto-accept. Timeout de 20s.
 */

import React, { useState, useEffect, useRef } from 'react';

const CHECKOUT_CONFIRM_TIMEOUT_MS = 20000; // 20 segundos

const CheckoutConfirm = ({ 
  visible, 
  playerName,     // Quien cerró
  closingScore,   // Puntuación del cierre
  isMe,           // true = yo cerré (esperando confirmación), false = mi rival cerró (debo confirmar)
  onConfirm,      // Confirmar cierre válido
  onDispute,      // Disputar cierre
  onTimeout,      // Timeout sin respuesta
}) => {
  const [timeLeft, setTimeLeft] = useState(20);
  const timerRef = useRef(null);
  const hasActed = useRef(false);

  useEffect(() => {
    if (!visible) {
      hasActed.current = false;
      return;
    }

    setTimeLeft(Math.ceil(CHECKOUT_CONFIRM_TIMEOUT_MS / 1000));

    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, Math.ceil((CHECKOUT_CONFIRM_TIMEOUT_MS - elapsed) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        // Solo el RIVAL (isMe=false) puede disparar el timeout del cliente.
        // El que cerró (isMe=true) solo muestra el countdown informativo;
        // el server tiene su propio timeout de 20s que auto-confirma.
        if (!hasActed.current && !isMe) {
          hasActed.current = true;
          onTimeout?.();
        }
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [visible, isMe, onTimeout]);

  if (!visible) return null;

  const handleConfirm = () => {
    if (hasActed.current) return;
    hasActed.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    onConfirm?.();
  };

  const handleDispute = () => {
    if (hasActed.current) return;
    hasActed.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    onDispute?.();
  };

  return (
    <div className="x01-modal-overlay checkout-confirm-overlay">
      <div className="x01-modal checkout-confirm-modal">
        {isMe ? (
          // Yo cerré → esperando que el rival confirme
          <>
            <div className="checkout-confirm-icon">🎯</div>
            <h2>¡Posible cierre!</h2>
            <p className="checkout-confirm-score">
              Has marcado <strong>{closingScore}</strong>
            </p>
            <div className="checkout-confirm-waiting">
              <div className="camera-setup-spinner small"></div>
              <p>Esperando confirmación del rival...</p>
              <div className={`checkout-confirm-timer ${timeLeft <= 5 ? 'critical' : ''}`}>
                {timeLeft}s
              </div>
            </div>
          </>
        ) : (
          // Mi rival cerró → debo confirmar
          <>
            <div className="checkout-confirm-icon">🎯</div>
            <h2>¡{playerName} dice que ha cerrado!</h2>
            <p className="checkout-confirm-score">
              Puntuación: <strong>{closingScore}</strong>
            </p>
            <p className="checkout-confirm-question">
              ¿Has visto el doble de cierre en el video?
            </p>
            <div className={`checkout-confirm-timer ${timeLeft <= 5 ? 'critical' : ''}`}>
              {timeLeft}s
            </div>
            <div className="checkout-confirm-actions">
              <button className="checkout-confirm-btn confirm" onClick={handleConfirm}>
                ✅ Confirmo el cierre
              </button>
              <button className="checkout-confirm-btn dispute" onClick={handleDispute}>
                ⚠️ No he visto el doble
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutConfirm;
